import { Injectable } from '@angular/core';
import { ModalOptions, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { take } from 'rxjs';

interface CountdownReloadBaseConfig {
  title: string;
  content: string;
  okText: string;
  seconds?: number;
  okDanger?: boolean;
  centered?: boolean;
  onReload?: () => void;
}

export interface CountdownReloadWarningConfig extends CountdownReloadBaseConfig {}

export interface CountdownReloadConfirmConfig extends CountdownReloadBaseConfig {
  cancelText: string;
  onCancel?: () => void;
}

@Injectable({
  providedIn: 'root',
})
export class ReloadModalService {
  private static readonly DEFAULT_SECONDS = 10;
  private static readonly INTERVAL_MS = 1_000;

  constructor(private readonly modalService: NzModalService) {}

  showWarning(config: CountdownReloadWarningConfig) {
    return this.openCountdownReloadModal(config, 'warning');
  }

  showConfirm(config: CountdownReloadConfirmConfig) {
    return this.openCountdownReloadModal(config, 'confirm');
  }

  private openCountdownReloadModal(
    config: CountdownReloadWarningConfig | CountdownReloadConfirmConfig,
    type: 'warning' | 'confirm'
  ) {
    const duration = Math.max(1, config.seconds ?? ReloadModalService.DEFAULT_SECONDS);
    let secondsRemaining = duration;
    let hasReloaded = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;
    let modalRef: NzModalRef | null = null;

    const clearCountdown = () => {
      if (intervalId !== undefined) {
        clearInterval(intervalId);
        intervalId = undefined;
      }
    };

    const reloadPage = () => {
      if (hasReloaded) {
        return;
      }

      hasReloaded = true;
      clearCountdown();
      config.onReload?.();
      window.location.reload();
    };

    const modalOptions: ModalOptions = {
      nzTitle: config.title,
      nzContent: config.content,
      nzOkText: this.getCountdownLabel(config.okText, secondsRemaining),
      nzOkDanger: config.okDanger ?? true,
      nzCentered: config.centered ?? true,
      nzOnOk: () => {
        reloadPage();
      },
    };

    if (type === 'confirm') {
      modalOptions.nzCancelText = (config as CountdownReloadConfirmConfig).cancelText;
      modalOptions.nzOnCancel = () => {
        clearCountdown();
        (config as CountdownReloadConfirmConfig).onCancel?.();
      };
      modalRef = this.modalService.confirm(modalOptions);
    } else {
      modalRef = this.modalService.warning(modalOptions);
    }

    modalRef.afterClose.pipe(take(1)).subscribe(() => {
      clearCountdown();
    });

    intervalId = setInterval(() => {
      secondsRemaining -= 1;

      if (secondsRemaining <= 0) {
        reloadPage();
        return;
      }

      modalRef?.updateConfig({
        nzOkText: this.getCountdownLabel(config.okText, secondsRemaining),
      });
    }, ReloadModalService.INTERVAL_MS);

    return modalRef;
  }

  private getCountdownLabel(label: string, secondsRemaining: number) {
    return `${label} (${secondsRemaining}s)`;
  }
}
